import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { supabase } from './lib/supabase';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import Hero from './components/home/Hero';
import Hello from './pages/Hello';
import Search from './components/Search';
import Vision from './components/Vision';

// Separate login component to use hooks
const AuthModal = ({ 
  showAuthModal, 
  setShowAuthModal, 
  setUser,
  email,
  setEmail,
  password,
  setPassword,
  authLoading,
  setAuthLoading,
  authError,
  setAuthError,
  isLoginMode,
  setIsLoginMode
}) => {
  const navigate = useNavigate();
  

  const handleLogin = async () => {
    setAuthLoading(true);
    setAuthError(null);
    try {
      let error;
      if (isLoginMode) {const { data, error: loginError } = await supabase.auth.signInWithPassword({ email, password });
          error = loginError;
          if (!error && data.user) {
            setUser(data.user);
            setShowAuthModal(false);
            navigate('/hello');
          }
      } else {
        const { error: signUpError } = await supabase.auth.signUp({ email, password });
        error = signUpError;
      }
      if (error) throw error;
    } catch (error) {
      setAuthError(error.message);
    } finally {
      setAuthLoading(false);
    }
  };

  if (!showAuthModal) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-70 z-50">
      <div className="bg-white p-6 rounded-lg shadow-lg w-96" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
        <h2 className="text-xl font-bold mb-4">{isLoginMode ? 'Login' : 'Sign Up'}</h2>
        {authError && <p className="text-red-600 mb-2">{authError}</p>}
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          className="w-full mb-2 p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
        />
        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          className="w-full mb-4 p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
        />
        <button
          onClick={handleLogin}
          disabled={authLoading}
          className="w-full bg-black hover:bg-gray-800 text-white px-4 py-2 rounded"
          style={{fontFamily: 'Space Grotesk, sans-serif'}}
        >
          {authLoading ? 'Please wait...' : (isLoginMode ? 'Login' : 'Sign Up')}
        </button>
        <p className="mt-4 text-center text-sm text-gray-600">
          {isLoginMode ? (
            <>Don't have an account? <button className="text-blue-600 underline" onClick={() => setIsLoginMode(false)}>Sign Up</button></>
          ) : (
            <>Already have an account? <button className="text-blue-600 underline" onClick={() => setIsLoginMode(true)}>Login</button></>
          )}
        </p>
        <button
          className="mt-4 w-full text-gray-700 hover:text-gray-900 transition-colors"
          style={{fontFamily: 'Space Grotesk, sans-serif'}}
          onClick={() => setShowAuthModal(false)}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const App = () => {
  const [user, setUser] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [isLoginMode, setIsLoginMode] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  useEffect(() => {
    const getSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setUser(session.user);
      } else {
        setUser(null);
      }
    };

    getSession();

    const { subscription } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        setUser(session.user);
        setShowAuthModal(false);
      } else {
        setUser(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  

  const handleLogout = async () => {
    const { error } = await supabase.auth.signOut();
    if (!error) setUser(null);
  };

  return (
    <Router>
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Header user={user} onLogout={handleLogout} />

        <div className="flex-grow">
          <Routes>
            <Route path="/" element={<Hero onSignInClick={() => setShowAuthModal(true)} />} />
            <Route 
              path="/hello" 
              element={
                user ? (
                  <Hello />
                ) : (
                  <Navigate to="/" replace />
                )
              } 
            />
            <Route 
              path="/search"
              element={
                user ? (
                  <Search />
                ) : (
                  <Navigate to="/" replace />
                )
              } 
            />
            <Route 
              path="/scan" 
              element={
                user ? (
                  <Vision />
                ) : (
                  <Navigate to="/" replace />
                )
              } 
            />
          </Routes>
        </div>

        <Footer 
          user={user}
          onLoginClick={() => setShowAuthModal(true)}
          onLogoutClick={handleLogout}
        />
        <AuthModal 
          showAuthModal={showAuthModal} 
          setShowAuthModal={setShowAuthModal}
          setUser={setUser}
          email={email}
          setEmail={setEmail}
          password={password}
          setPassword={setPassword}
          authLoading={authLoading}
          setAuthLoading={setAuthLoading}
          authError={authError}
          setAuthError={setAuthError}
          isLoginMode={isLoginMode}
          setIsLoginMode={setIsLoginMode}
        />
      </div>
    </Router>
  );
};

export default App;